import {FC, useRef, useState, useEffect} from 'react'
import {Copy01Icon, DotsHorizontalIcon, Pencil01Icon, Trash01Icon} from '@components/ui/icon/Icon'
import {useTranslation} from '@/translations/i18n'
import {StyledMenuContainer, StyledMenuGroup, StyledMenuItem} from './style'
import {Button} from '@/components/ui/button/Button'
import {DuplicateViewDialog} from '../duplicate-view-dialog/DuplicateViewDialog'
import {ViewEntity, View} from '../../types'
import {useSelectContext} from '@ariakit/react'
import {raise} from '@/utilities/helpers'
import {EditViewDialog} from '../edit-view-dialog/EditViewDialog'
import {DeleteViewDialog} from '../delete-view-dialog/DeleteViewDialog'

/* This component is manually built without ariakit because the virtual list conflicts with it */
export const RowMenu: FC<{triggerRef: React.RefObject<HTMLSelectElement>; entity: ViewEntity; view: View}> = ({
    triggerRef,
    entity,
    view
}) => {
    const {t} = useTranslation()
    const [isOpen, setIsOpen] = useState(false)
    const menuRef = useRef<HTMLDivElement>(null)
    const buttonRef = useRef<HTMLButtonElement>(null)
    const [menuPosition, setMenuPosition] = useState({top: 0, left: 0})

    const {setOpen: setSelectOpen} = useSelectContext() ?? raise('RowMenu must be in a SelectProvider')

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                menuRef.current &&
                !menuRef.current.contains(event.target as Node) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const handleButtonClick = (event: React.MouseEvent) => {
        event.stopPropagation()

        if (buttonRef.current && triggerRef.current) {
            const buttonRect = buttonRef.current.getBoundingClientRect()
            const triggerRect = triggerRef.current.getBoundingClientRect()

            setMenuPosition({
                top: buttonRect.top - triggerRect.top,
                left: buttonRect.left - triggerRect.left
            })
        }

        setIsOpen(!isOpen)
    }

    const onClickItem = () => {
        setIsOpen(false)
        setSelectOpen(false)
    }

    return (
        <>
            <Button
                ref={buttonRef}
                className="actions-button"
                size="xs"
                shape="square"
                variant="secondary"
                onClick={handleButtonClick}
            >
                <DotsHorizontalIcon />
            </Button>
            <StyledMenuContainer
                ref={menuRef}
                $isOpen={isOpen}
                style={menuPosition}
                $width={'200px'}
                onClick={event => event.stopPropagation()}
            >
                <StyledMenuGroup>
                    <EditViewDialog
                        entity={entity}
                        view={view}
                        id={view.id}
                        trigger={
                            <StyledMenuItem onClick={onClickItem}>
                                <Pencil01Icon />
                                {t('commons:edit')}
                            </StyledMenuItem>
                        }
                    />
                    <DuplicateViewDialog
                        entity={entity}
                        view={view}
                        trigger={
                            <StyledMenuItem onClick={onClickItem}>
                                <Copy01Icon />
                                {t('commons:duplicate')}
                            </StyledMenuItem>
                        }
                    />
                </StyledMenuGroup>
                <StyledMenuGroup>
                    <DeleteViewDialog
                        view={view}
                        trigger={
                            <StyledMenuItem $variant="danger" onClick={onClickItem}>
                                <Trash01Icon />
                                {t('commons:delete')}
                            </StyledMenuItem>
                        }
                    />
                </StyledMenuGroup>
            </StyledMenuContainer>
        </>
    )
}
